import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

/* ------------------------------------------------------------------ */
/*  Public appointment page (PRD.255)                                 */
/*                                                                    */
/*  Unauthenticated, mobile-first, display-only. Reached from the     */
/*  WhatsApp reminder's dynamic URL button (/a/:appointmentId). Does   */
/*  NOT use PowerSync or Firebase auth — it fetches a minimal payload  */
/*  from the public-links Cloud Function. Confirm/Cancel happen by     */
/*  replying to the WhatsApp message, not here.                       */
/* ------------------------------------------------------------------ */

const ENDPOINT = import.meta.env.VITE_PUBLIC_APPOINTMENT_URL;
const BRAND_COLOR = import.meta.env.VITE_PRIMARY_COLOR || '#2563eb';

interface PublicAppointment {
  appointmentId: string;
  clinicName: string | null;
  providerName: string | null;
  serviceName: string | null;
  patientFirstName: string | null;
  date: string | null;
  startTime: string | null;
  statusLabel: string | null;
  whatsappResponseStatus: string | null;
  whatsappResponseText: string | null;
  whatsappResponseAt: string | null;
  isPast: boolean;
  isCancelled: boolean;
  expiresAt: string | null;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: PublicAppointment }
  | { kind: 'not_found' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string };

export function PublicAppointmentPage() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  // Discourage indexing of a public, PHI-adjacent page.
  useEffect(() => {
    document.title = 'Your appointment';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!appointmentId) {
      setState({ kind: 'not_found' });
      return;
    }
    if (!ENDPOINT) {
      setState({ kind: 'error', message: 'This page is not configured.' });
      return;
    }
    setState({ kind: 'loading' });
    fetch(`${ENDPOINT}?id=${encodeURIComponent(appointmentId)}`, { method: 'GET' })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) return setState({ kind: 'not_found' });
        if (res.status === 410) return setState({ kind: 'expired' });
        if (!res.ok) return setState({ kind: 'error', message: 'Something went wrong.' });
        const data = (await res.json()) as PublicAppointment;
        setState({ kind: 'ok', data });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error', message: 'Could not load your appointment.' });
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ ...accentBarStyle, background: BRAND_COLOR }} />
        {state.kind === 'loading' && <Centered>Loading your appointment…</Centered>}
        {state.kind === 'not_found' && (
          <Message
            title="Appointment not found"
            body="We couldn't find this appointment. Please contact the clinic if you need help."
          />
        )}
        {state.kind === 'expired' && (
          <Message
            title="This link has expired"
            body="This appointment link is no longer available. Please contact the clinic for details."
          />
        )}
        {state.kind === 'error' && <Message title="Something went wrong" body={state.message} />}
        {state.kind === 'ok' && <AppointmentDetails data={state.data} />}
      </div>
    </div>
  );
}

function AppointmentDetails({ data }: { data: PublicAppointment }) {
  const dateLabel = formatDate(data.date);
  const timeLabel = formatTime(data.startTime);

  return (
    <div style={{ padding: '24px 20px 28px' }}>
      <p style={greetingStyle}>
        {data.patientFirstName ? `Hi ${data.patientFirstName},` : 'Hello,'}
      </p>
      <h1 style={titleStyle}>Your appointment</h1>

      {data.isCancelled && <Banner tone="warn">This appointment has been cancelled.</Banner>}
      {!data.isCancelled && data.isPast && (
        <Banner tone="muted">This appointment has already passed.</Banner>
      )}
      {data.whatsappResponseStatus === 'confirmed' && (
        <Banner tone="ok">You confirmed this appointment. Thank you!</Banner>
      )}
      {data.whatsappResponseStatus === 'cancellation_requested' && (
        <Banner tone="warn">
          You asked to cancel. Our clinic will follow up to confirm or reschedule.
        </Banner>
      )}

      <dl style={{ margin: '20px 0 0' }}>
        <Row label="When" value={[dateLabel, timeLabel].filter(Boolean).join(' · ') || '—'} />
        {data.providerName && <Row label="With" value={data.providerName} />}
        {data.serviceName && <Row label="For" value={data.serviceName} />}
        {data.clinicName && <Row label="Clinic" value={data.clinicName} />}
        {data.statusLabel && <Row label="Status" value={data.statusLabel} />}
      </dl>

      <p style={footnoteStyle}>
        To confirm or reschedule, reply to the WhatsApp message we sent you.
      </p>
    </div>
  );
}

/* ------------------------------- bits ----------------------------- */

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={rowStyle}>
      <dt style={dtStyle}>{label}</dt>
      <dd style={ddStyle}>{value}</dd>
    </div>
  );
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ padding: '40px 24px', textAlign: 'center' }}>
      <h1 style={{ ...titleStyle, marginTop: 0 }}>{title}</h1>
      <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.6, margin: 0 }}>{body}</p>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '48px 24px', textAlign: 'center', color: '#64748b', fontSize: 15 }}>
      {children}
    </div>
  );
}

function Banner({ tone, children }: { tone: 'ok' | 'warn' | 'muted'; children: React.ReactNode }) {
  const palette = {
    ok: { bg: '#dcfce7', fg: '#166534' },
    warn: { bg: '#fef3c7', fg: '#92400e' },
    muted: { bg: '#f1f5f9', fg: '#475569' },
  }[tone];
  return (
    <div
      style={{
        background: palette.bg,
        color: palette.fg,
        borderRadius: 10,
        padding: '10px 14px',
        fontSize: 14,
        lineHeight: 1.5,
        marginTop: 14,
      }}
    >
      {children}
    </div>
  );
}

/* ----------------------------- format ----------------------------- */

function formatDate(date: string | null): string {
  if (!date) return '';
  const [y, m, d] = date.split('-').map(Number);
  if ([y, m, d].some((n) => Number.isNaN(n))) return date;
  return new Intl.DateTimeFormat('en-PH', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(y, m - 1, d));
}

function formatTime(time: string | null): string {
  if (!time) return '';
  const [hh, mm] = time.split(':').map(Number);
  if ([hh, mm].some((n) => Number.isNaN(n))) return time;
  return new Intl.DateTimeFormat('en-PH', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(2000, 0, 1, hh, mm));
}

/* ----------------------------- styles ----------------------------- */

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#f3f4f7',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '24px 16px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};
const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.06)',
  overflow: 'hidden',
};
const accentBarStyle: React.CSSProperties = { height: 6, width: '100%' };
const greetingStyle: React.CSSProperties = { margin: 0, color: '#64748b', fontSize: 15 };
const titleStyle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: 22,
  fontWeight: 700,
  color: '#0f172a',
};
const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
  padding: '12px 0',
  borderTop: '1px solid #f1f5f9',
};
const dtStyle: React.CSSProperties = { margin: 0, color: '#94a3b8', fontSize: 13 };
const ddStyle: React.CSSProperties = {
  margin: 0,
  color: '#0f172a',
  fontSize: 15,
  fontWeight: 600,
  textAlign: 'right',
};
const footnoteStyle: React.CSSProperties = {
  marginTop: 22,
  color: '#94a3b8',
  fontSize: 13,
  lineHeight: 1.6,
};
